import type { SandGrid } from '../sandgrid/SandGrid';
import type { DustParticleSystem } from '../particles/DustParticleSystem';

export class SandPhysics {
  private angleOfRepose: number;
  private collapseRate: number;
  private cohesion: number;

  constructor(angleOfRepose: number = 35, collapseRate: number = 0.5, cohesion: number = 0.0) {
    this.angleOfRepose = angleOfRepose;
    this.collapseRate = collapseRate;
    this.cohesion = cohesion;
  }

  public setAngleOfRepose(angle: number): void {
    this.angleOfRepose = angle;
  }

  public setCollapseRate(rate: number): void {
    this.collapseRate = rate;
  }

  public setCohesion(cohesion: number): void {
    this.cohesion = cohesion;
  }

  public updateStability(
    sandGrid: SandGrid,
    deltaTime: number,
    dustParticles?: DustParticleSystem
  ): void {
    // Cohesion adds resistance to collapse - higher cohesion means steeper angles before collapse
    const criticalHeight = Math.tan((this.angleOfRepose * Math.PI) / 180) + this.cohesion;

    const changes: Array<{ x: number; y: number; delta: number }> = [];

    // Use random-by-line order to reduce visual patterns in collapse
    sandGrid.apply((x, y, height) => {
      if (height <= 0.1) return;

      const neighbors = sandGrid.getNeighbors(x, y);

      if (neighbors.length === 0) return;

      let maxDiff = 0;
      let lowestNeighbor: { x: number; y: number; value: number } | null = null;

      for (const neighbor of neighbors) {
        const diff = height - neighbor.value;
        if (diff > maxDiff) {
          maxDiff = diff;
          lowestNeighbor = neighbor;
        }
      }

      if (maxDiff > criticalHeight && lowestNeighbor) {
        const excessHeight = maxDiff - criticalHeight;
        const transferAmount = Math.min(
          excessHeight * this.collapseRate * deltaTime * 60,
          height * 0.5
        );

        changes.push(
          { x, y, delta: -transferAmount },
          {
            x: lowestNeighbor.x,
            y: lowestNeighbor.y,
            delta: transferAmount
          }
        );

        // Emit dust particles on significant collapses
        if (dustParticles && transferAmount > 0.5) {
          const intensity = Math.min(transferAmount / 3, 1.0);
          dustParticles.emitDustBurst(
            x - sandGrid.width / 2,
            y - sandGrid.height / 2,
            height,
            intensity
          );
        }
      }
    }, 'random-by-line');

    for (const change of changes) {
      sandGrid.add(change.x, change.y, change.delta);
    }
  }
}
